import React, { useEffect, useMemo, useState, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { MdToken, MdOutlineTrendingUp, MdOutlineDateRange, MdBarChart, MdShowChart } from "react-icons/md";
import { FiRefreshCw, FiCalendar } from "react-icons/fi";
import UserService from "../../services/user";
import { toastError, toastSuccess } from "../../components/ToastNotification";

const TRANSLATIONS = {
  EN: {
    modalTitle: "Token Management & Analytics",
    userSubtitle: "Manage balance and inspect token consumption history",
    updateSection: "Update Token Balance",
    analyticsSection: "Token ",
    labelToken: "Token Amount",
    labelEndDate: "End Date",
    placeholderToken: "Enter token amount",
    cancel: "Cancel",
    update: "Update Token",
    updating: "Updating...",
    allocatedTokens: "Allocated",
    usedTokens: "All-time Used",
    remainingTokens: "Remaining",
    periodUsage: "Period Usage",
    inputTokens: "Input Tokens",
    outputTokens: "Output Tokens",
    startDate: "Start Date",
    endDate: "End Date",
    filterDate: "Filter by Date Range",
    clearDate: "Clear Dates",
    chartArea: "Trend",
    chartBar: "Bars",
    noUsageTitle: "No Token Usage Recorded",
    noUsageDesc: "No token consumption activity was found for this user in the selected period.",
    dailyLog: "Daily Consumption",
    colDate: "Date",
    colInput: "Input",
    colOutput: "Output",
    colTotal: "Total Used",
    colLastUpdated: "Updated At",
    toastUpdated: "Token updated successfully!",
    toastUsageError: "Failed to load token usage history",
    viewAll: "Show All",
  },
  ES: {
    modalTitle: "Gestión y Análisis de Tokens",
    userSubtitle: "Administra el saldo e inspecciona el historial de consumo de tokens",
    updateSection: "Actualizar Saldo de Tokens",
    analyticsSection: "Consumo de Tokens en el Tiempo",
    labelToken: "Cantidad de Tokens",
    labelEndDate: "Fecha de Fin",
    placeholderToken: "Ingresa la cantidad de tokens",
    cancel: "Cancelar",
    update: "Actualizar Token",
    updating: "Actualizando...",
    allocatedTokens: "Asignados",
    usedTokens: "Usados Histórico",
    remainingTokens: "Restantes",
    periodUsage: "Uso del Período",
    inputTokens: "Tokens de Entrada",
    outputTokens: "Tokens de Salida",
    startDate: "Fecha Inicio",
    endDate: "Fecha Fin",
    filterDate: "Filtrar por Rango de Fechas",
    clearDate: "Limpiar Fechas",
    chartArea: "Tendencia",
    chartBar: "Barras",
    noUsageTitle: "Sin Uso de Tokens Registrado",
    noUsageDesc: "No se encontró actividad de consumo de tokens para este usuario en el período seleccionado.",
    dailyLog: "Registro Diario de Consumo",
    colDate: "Fecha",
    colInput: "Entrada",
    colOutput: "Salida",
    colTotal: "Total Usado",
    colLastUpdated: "Actualizado",
    toastUpdated: "¡Token actualizado exitosamente!",
    toastUsageError: "Error al cargar el historial de uso de tokens",
    viewAll: "Ver Todos",
  },
};

const formatNumber = (num) => {
  if (num == null || isNaN(num)) return "0";
  return Number(num).toLocaleString();
};

const formatCompact = (num) => {
  if (num == null || isNaN(num)) return "0";
  const n = Number(num);
  if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return Math.round(n).toLocaleString();
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const formatChartDate = (dateStr, isMultiYear = false) => {
  if (!dateStr) return "";
  const clean = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
  const parts = clean.split("-");

  if (parts.length === 3) {
    const year = parts[0];
    const monthIdx = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const month = MONTH_NAMES[monthIdx] || parts[1];
    const shortYear = year.length === 4 ? year.slice(2) : year;

    if (isMultiYear) {
      return `${day} ${month} '${shortYear}`;
    }
    return `${day} ${month}`;
  }

  if (parts.length === 2) {
    const year = parts[0];
    const monthIdx = parseInt(parts[1], 10) - 1;
    const month = MONTH_NAMES[monthIdx] || parts[1];
    const shortYear = year.length === 4 ? year.slice(2) : year;

    return isMultiYear ? `${month} '${shortYear}` : `${month} ${year}`;
  }

  return dateStr;
};

const formatTooltipDate = (dateStr) => {
  if (!dateStr) return "";
  const clean = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
  const parts = clean.split("-");

  if (parts.length === 3) {
    const year = parts[0];
    const monthIdx = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const month = MONTH_NAMES[monthIdx] || parts[1];
    return `${day} ${month} ${year}`;
  }

  if (parts.length === 2) {
    const year = parts[0];
    const monthIdx = parseInt(parts[1], 10) - 1;
    const month = MONTH_NAMES[monthIdx] || parts[1];
    return `${month} ${year}`;
  }

  return dateStr;
};

const sanitizeDate = (val) => {
  if (!val) return "";
  const parts = val.split("-");
  if (parts[0] && parts[0].length > 4) {
    parts[0] = parts[0].slice(0, 4);
    return parts.join("-");
  }
  return val;
};

const TokenModal = ({
  isOpen,
  onClose,
  user,
  onTokenUpdated,
  lang = "EN",
}) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.EN;

  // Update token form state
  const [tokenAmount, setTokenAmount] = useState("");
  const [tokenEndDate, setTokenEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Daily token usage API state
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageList, setUsageList] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartType, setChartType] = useState("area"); // "area" | "bar"
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const debounceTimerRef = useRef(null);
  const lastFetchedParamsRef = useRef({ sDate: null, eDate: null, userId: null });

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load daily token usage when modal opens or user changes
  useEffect(() => {
    if (isOpen && user?._id) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setTokenAmount("");
      setTokenEndDate("");
      lastFetchedParamsRef.current = { sDate: null, eDate: null, userId: null };
      fetchDailyUsage(startDate, endDate);
    } else {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setUsageList([]);
      setStartDate("");
      setEndDate("");
      setHoveredPoint(null);
      lastFetchedParamsRef.current = { sDate: null, eDate: null, userId: null };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?._id]);

  const isValidDateStr = (str) => {
    if (!str) return true;
    if (str.length !== 10) return false;
    const parts = str.split("-");
    if (parts.length !== 3) return false;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
    return y >= 1900 && y <= 2099 && m >= 1 && m <= 12 && d >= 1 && d <= 31;
  };

  const fetchDailyUsage = async (sDate = startDate, eDate = endDate, force = false) => {
    if (!user?._id) return;

    // Do not call API if partial or invalid date is entered
    if (!isValidDateStr(sDate) || !isValidDateStr(eDate)) {
      return;
    }

    // Prevent duplicate API calls with identical parameters
    if (
      !force &&
      lastFetchedParamsRef.current.sDate === sDate &&
      lastFetchedParamsRef.current.eDate === eDate &&
      lastFetchedParamsRef.current.userId === user._id
    ) {
      return;
    }

    lastFetchedParamsRef.current = { sDate, eDate, userId: user._id };
    setUsageLoading(true);
    try {
      const params = {
        page: 1,
        limit: 1000,
        search: user.email || user.firstName || "",
      };
      if (sDate) {
        params.startDate = sDate;
      }
      if (eDate) {
        params.endDate = eDate;
      }
      const response = await UserService.getDailyTokenUsage(params);
      const rawData = Array.isArray(response?.data?.data) ? response.data.data : [];

      // Safeguard: filter specifically for this user if multiple match search
      const userRecords = rawData.filter((item) => {
        if (!item.userId) return true;
        if (typeof item.userId === "string") return item.userId === user._id;
        return (
          (user._id && item.userId._id === user._id) ||
          (user.email && item.userId.email?.toLowerCase() === user.email?.toLowerCase())
        );
      });

      setUsageList(userRecords);
    } catch (error) {
      console.error("Error fetching daily token usage:", error);
      toastError(t.toastUsageError);
    } finally {
      setUsageLoading(false);
    }
  };

  const debouncedFetchDailyUsage = (sDate, eDate, delay = 500) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchDailyUsage(sDate, eDate);
    }, delay);
  };

  const handleStartDateChange = (e) => {
    const val = sanitizeDate(e.target.value);
    setStartDate(val);
    if (!val || val.length === 10) {
      debouncedFetchDailyUsage(val, endDate);
    }
  };

  const handleEndDateChange = (e) => {
    const val = sanitizeDate(e.target.value);
    setEndDate(val);
    if (!val || val.length === 10) {
      debouncedFetchDailyUsage(startDate, val);
    }
  };

  const handleClearDates = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setStartDate("");
    setEndDate("");
    fetchDailyUsage("", "", true);
  };

  const handleTokenSubmit = async (e) => {
    e?.preventDefault();
    if (!user?._id || !tokenAmount) return;
    setIsSubmitting(true);
    try {
      const body = {
        userId: user._id,
        token: Number(tokenAmount),
      };
      if (tokenEndDate) {
        body.endDate = tokenEndDate;
      }
      await UserService.updateToken(body);
      toastSuccess(t.toastUpdated);
      if (onTokenUpdated) onTokenUpdated();
      onClose();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update token");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group and sort data by date for chart plotting
  const chartData = useMemo(() => {
    if (!usageList || usageList.length === 0) return [];

    const map = {};
    usageList.forEach((item) => {
      const d = item.date || (item.createdAt ? item.createdAt.split("T")[0] : "");
      if (!d) return;
      if (!map[d]) {
        map[d] = {
          date: d,
          totalUsedToken: 0,
          inputToken: 0,
          outputToken: 0,
          rawList: [],
        };
      }
      map[d].totalUsedToken += Number(item.totalUsedToken || 0);
      map[d].inputToken += Number(item.inputToken || 0);
      map[d].outputToken += Number(item.outputToken || 0);
      map[d].rawList.push(item);
    });

    // Chronological sort: oldest to newest (left to right)
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [usageList]);

  // Aggregate stats
  const stats = useMemo(() => {
    let totalUsed = 0;
    let totalInput = 0;
    let totalOutput = 0;
    chartData.forEach((d) => {
      totalUsed += d.totalUsedToken;
      totalInput += d.inputToken;
      totalOutput += d.outputToken;
    });
    return { totalUsed, totalInput, totalOutput };
  }, [chartData]);

  if (!isOpen || !user) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-3 sm:p-5"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-primary/10 transition-all animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary/10 bg-gradient-to-r from-primary/5 via-white to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-sm">
              <MdToken className="text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-roboto font-bold text-lg text-primary tracking-wide">
                  {t.modalTitle}
                </h2>

              </div>
              <p className="font-roboto text-xs text-gray-500 mt-0.5">
                {user.email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-primary hover:bg-[#F0EEF8] transition-colors"
          >
            <IoMdClose className="text-xl" />
          </button>
        </div>

        {/* Modal Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 lg:p-6 flex flex-col gap-6">

          {/* Quick Balance Summary Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#F0EEF8]/60 border border-primary/15 rounded-2xl p-3.5 flex flex-col justify-between">
              <span className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t.allocatedTokens}
              </span>
              <p className="font-roboto font-bold text-xl text-primary mt-1">
                {formatCompact(user.total_tokens)}
              </p>
              <span className="text-[11px] text-gray-400 mt-0.5 font-mono">
                {formatNumber(user.total_tokens)} tokens
              </span>
            </div>

            <div className="bg-[#F0EEF8]/60 border border-primary/15 rounded-2xl p-3.5 flex flex-col justify-between">
              <span className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t.usedTokens}
              </span>
              <p className="font-roboto font-bold text-xl text-red-500 mt-1">
                {formatCompact(user.used_tokens)}
              </p>
              <span className="text-[11px] text-gray-400 mt-0.5 font-mono">
                {formatNumber(user.used_tokens)} tokens
              </span>
            </div>

            <div className="bg-emerald-50/60 border border-emerald-200/70 rounded-2xl p-3.5 flex flex-col justify-between">
              <span className="font-roboto text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                {t.remainingTokens}
              </span>
              <p className="font-roboto font-bold text-xl text-emerald-600 mt-1">
                {formatCompact(Math.max(0, (user.total_tokens || 0) - (user.used_tokens || 0)))}
              </p>
              <span className="text-[11px] text-emerald-600/70 mt-0.5 font-mono">
                {formatNumber(Math.max(0, (user.total_tokens || 0) - (user.used_tokens || 0)))} tokens
              </span>
            </div>
          </div>

          {/* Main 2-Column Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

            {/* Left Column: Update Token Form */}
            <div className="lg:col-span-4 bg-white border border-primary/15 rounded-2xl p-5 shadow-sm flex flex-col gap-4">
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <MdToken className="text-base" />
                </div>
                <h3 className="font-roboto font-bold text-sm text-primary uppercase tracking-wide">
                  {t.updateSection}
                </h3>
              </div>

              <form onSubmit={handleTokenSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="font-roboto text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center justify-between">
                    <span>{t.labelToken} <span className="text-red-500">*</span></span>
                    <span className="text-[11px] font-normal text-gray-400">Total balance</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={tokenAmount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || Number(val) >= 0) {
                        setTokenAmount(val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="w-full rounded-xl px-4 py-2.5 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/40 text-primary font-roboto text-sm placeholder-gray-400"
                    placeholder={t.placeholderToken}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="font-roboto text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t.labelEndDate}
                  </label>
                  <input
                    type="date"
                    value={tokenEndDate}
                    onChange={(e) => setTokenEndDate(sanitizeDate(e.target.value))}
                    min={new Date().toISOString().split("T")[0]}
                    max="2099-12-31"
                    className="w-full rounded-xl px-4 py-2.5 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/40 text-primary font-roboto text-sm"
                  />
                  <span className="text-[11px] text-gray-400">
                    Leave blank to keep existing expiry
                  </span>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-2.5 rounded-xl font-roboto font-semibold text-xs uppercase tracking-wide text-gray-500 border border-primary/20 hover:bg-[#F0EEF8] transition-all text-center"
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !tokenAmount}
                    className="flex-1 py-2.5 rounded-xl font-roboto font-bold text-xs uppercase tracking-wide text-white bg-primary hover:bg-primary/90 transition-all disabled:opacity-50 shadow-sm flex items-center justify-center gap-1.5"
                  >
                    {isSubmitting ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      t.update
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Daily Usage Analytics & Graph */}
            <div className="lg:col-span-8 bg-white border border-primary/15 rounded-2xl p-5 shadow-sm flex flex-col gap-4">

              {/* Analytics Header & Date Filter Toolbar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-primary/10">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <MdOutlineTrendingUp className="text-base" />
                  </div>
                  <div>
                    <h3 className="font-roboto font-bold text-sm text-primary uppercase tracking-wide">
                      {t.analyticsSection}
                    </h3>
                  </div>
                </div>

                {/* Filter Controls & View Actions */}
                <div className="flex items-center gap-2 justify-start sm:justify-end flex-wrap sm:flex-nowrap">
                  {/* Start Date */}
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      min="2000-01-01"
                      max={endDate || new Date().toISOString().split("T")[0]}
                      title={t.startDate}
                      className="px-2 py-1.5 rounded-xl border border-primary/20 text-xs font-roboto text-gray-700 bg-[#F0EEF8]/40 focus:outline-none focus:ring-2 focus:ring-primary/20 w-[118px]"
                    />
                  </div>

                  <span className="text-gray-400 text-xs font-medium">-</span>

                  {/* End Date */}
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      min={startDate || "2000-01-01"}
                      max={new Date().toISOString().split("T")[0]}
                      title={t.endDate}
                      className="px-2 py-1.5 rounded-xl border border-primary/20 text-xs font-roboto text-gray-700 bg-[#F0EEF8]/40 focus:outline-none focus:ring-2 focus:ring-primary/20 w-[118px]"
                    />
                  </div>

                  {/* Clear Cross Icon Button */}
                  {(startDate || endDate) && (
                    <button
                      type="button"
                      onClick={handleClearDates}
                      title={t.clearDate}
                      className="w-7 h-7 rounded-xl flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 border border-primary/15 transition-all flex-shrink-0"
                    >
                      <IoMdClose className="text-sm" />
                    </button>
                  )}

                  {/* Chart type toggle */}
                  <div className="flex items-center border border-primary/20 rounded-xl overflow-hidden p-0.5 bg-gray-50 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setChartType("area")}
                      title={t.chartArea}
                      className={`px-2 py-1 text-xs rounded-lg font-roboto font-medium flex items-center gap-1 transition-all ${chartType === "area"
                          ? "bg-primary text-white shadow-xs"
                          : "text-gray-500 hover:text-primary"
                        }`}
                    >
                      <MdShowChart className="text-sm" />
                      <span className="hidden sm:inline">{t.chartArea}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setChartType("bar")}
                      title={t.chartBar}
                      className={`px-2 py-1 text-xs rounded-lg font-roboto font-medium flex items-center gap-1 transition-all ${chartType === "bar"
                          ? "bg-primary text-white shadow-xs"
                          : "text-gray-500 hover:text-primary"
                        }`}
                    >
                      <MdBarChart className="text-sm" />
                      <span className="hidden sm:inline">{t.chartBar}</span>
                    </button>
                  </div>

                  {/* Refresh Button */}
                  <button
                    type="button"
                    onClick={() => fetchDailyUsage(startDate, endDate)}
                    disabled={usageLoading}
                    className="p-2 rounded-xl text-gray-400 hover:text-primary hover:bg-[#F0EEF8] transition-colors flex-shrink-0"
                    title="Refresh"
                  >
                    <FiRefreshCw className={`text-sm ${usageLoading ? "animate-spin text-primary" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Chart Body */}
              <div className="relative min-h-[240px] flex flex-col justify-center">
                {usageLoading ? (
                  <UsageChartSkeleton type={chartType} />
                ) : chartData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-[#F0EEF8] flex items-center justify-center text-primary/40 mb-3">
                      <MdOutlineDateRange className="text-3xl" />
                    </div>
                    <p className="font-roboto font-semibold text-sm text-gray-700">
                      {t.noUsageTitle}
                    </p>
                    <p className="font-roboto text-xs text-gray-400 mt-1 max-w-sm">
                      {t.noUsageDesc}
                    </p>
                    {(startDate || endDate) && (
                      <button
                        type="button"
                        onClick={handleClearDates}
                        className="mt-3 px-3 py-1.5 text-xs rounded-xl bg-primary text-white font-roboto font-semibold hover:bg-primary/90 transition-all"
                      >
                        {t.viewAll}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-full">
                    {/* SVG Chart Rendering */}
                    <UsageSvgChart
                      data={chartData}
                      type={chartType}
                      hoveredPoint={hoveredPoint}
                      setHoveredPoint={setHoveredPoint}
                    />

                    {/* Chart Legend */}
                    <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-gray-100 text-xs font-roboto">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-600 font-medium">{t.colTotal}:</span>
                        <span className="font-bold text-primary font-mono">{formatNumber(stats.totalUsed)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-600 font-medium">{t.inputTokens}:</span>
                        <span className="font-bold text-sky-600 font-mono">{formatNumber(stats.totalInput)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-600 font-medium">{t.outputTokens}:</span>
                        <span className="font-bold text-emerald-600 font-mono">{formatNumber(stats.totalOutput)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Detailed Daily Breakdown Table (if multiple entries) */}
              {!usageLoading && chartData.length > 0 && (
                <div className="mt-2 pt-3 border-t border-primary/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-roboto font-bold text-xs text-gray-500 uppercase tracking-wide">
                      {t.dailyLog}
                    </span>
                  </div>
                  <div className="overflow-x-auto max-h-48 rounded-xl border border-gray-100">
                    <table className="w-full text-left font-roboto text-xs">
                      <thead className="bg-[#F0EEF8]/70 text-gray-600 uppercase font-semibold sticky top-0">
                        <tr>
                          <th className="px-3 py-2">{t.colDate}</th>
                          <th className="px-3 py-2 text-right">{t.colInput}</th>
                          <th className="px-3 py-2 text-right">{t.colOutput}</th>
                          <th className="px-3 py-2 text-right">{t.colTotal}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {chartData.map((row, idx) => (
                          <tr
                            key={row.date}
                            className={`hover:bg-[#F0EEF8]/40 transition-colors ${idx % 2 === 1 ? "bg-gray-50/40" : ""
                              }`}
                          >
                            <td className="px-3 py-2 font-medium text-gray-800 font-mono">
                              {row.date}
                            </td>
                            <td className="px-3 py-2 text-right text-sky-600 font-mono">
                              {formatNumber(row.inputToken)}
                            </td>
                            <td className="px-3 py-2 text-right text-emerald-600 font-mono">
                              {formatNumber(row.outputToken)}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-primary font-mono">
                              {formatNumber(row.totalUsedToken)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

/* ── Graph & Daily Log Shimmer Skeleton Sub-Component ───────────── */
const UsageChartSkeleton = ({ type = "area" }) => {
  return (
    <div className="w-full flex flex-col gap-4 py-1 select-none">
      {/* Skeleton Chart Box */}
      <div className="relative w-full h-[220px] bg-gradient-to-b from-[#F0EEF8]/40 via-white to-[#F0EEF8]/30 rounded-2xl border border-primary/10 p-3.5 flex flex-col justify-between overflow-hidden animate-shimmer shadow-xs">
        {/* Y-axis ticks + horizontal guide lines */}
        <div className="relative w-full h-[155px] flex flex-col justify-between">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="flex items-center gap-2.5 w-full">
              <div className="w-7 h-2 bg-primary/20 rounded-md" />
              <div className="flex-1 border-b border-dashed border-primary/15" />
            </div>
          ))}

          {/* Shimmer Bar / Area Simulation */}
          {type === "bar" ? (
            <div className="absolute inset-x-10 bottom-0 top-3 flex items-end justify-between gap-2 px-1">
              {[35, 65, 45, 80, 55, 90, 70, 85, 40].map((h, idx) => (
                <div
                  key={idx}
                  className="flex-1 max-w-[34px] bg-gradient-to-t from-primary/30 via-primary/15 to-primary/5 rounded-t-lg"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-x-10 bottom-0 top-3 flex items-end justify-between gap-1 px-1">
              <svg className="w-full h-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 500 150">
                <defs>
                  <linearGradient id="skeletonGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#534694" stopOpacity="0.25" />
                    <stop offset="50%" stopColor="#7B61FF" stopOpacity="0.1" />
                    <stop offset="100%" stopColor="#534694" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0,110 C 60,60 120,130 180,45 C 240,110 300,20 360,65 C 420,30 460,80 500,40 L 500,150 L 0,150 Z"
                  fill="url(#skeletonGrad)"
                />
                <path
                  d="M 0,110 C 60,60 120,130 180,45 C 240,110 300,20 360,65 C 420,30 460,80 500,40"
                  fill="none"
                  stroke="#534694"
                  strokeWidth="2.5"
                  strokeOpacity="0.3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}
        </div>

        {/* X-axis date pills skeleton */}
        <div className="flex items-center justify-between pl-9 pr-1 pt-2.5 border-t border-primary/15">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="w-8 sm:w-10 h-2 bg-primary/20 rounded-md" />
          ))}
        </div>
      </div>

      {/* Skeleton Legend */}
      <div className="flex items-center justify-center gap-6 pt-2 border-t border-gray-100 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="w-16 h-3 bg-gray-200 rounded-md" />
          <div className="w-12 h-3 bg-primary/20 rounded-md" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-3 bg-gray-200 rounded-md" />
          <div className="w-12 h-3 bg-sky-200 rounded-md" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-3 bg-gray-200 rounded-md" />
          <div className="w-12 h-3 bg-emerald-200 rounded-md" />
        </div>
      </div>

      {/* Skeleton Daily Consumption Table */}
      <div className="mt-1 pt-3 border-t border-primary/10 flex flex-col gap-2 animate-shimmer">
        <div className="w-32 h-3 bg-gray-200 rounded-md mb-0.5" />
        <div className="rounded-xl border border-gray-100 overflow-hidden">
          <div className="h-8 bg-[#F0EEF8]/70 flex items-center px-4 justify-between border-b border-gray-100">
            <div className="w-12 h-2.5 bg-gray-300/80 rounded-md" />
            <div className="w-12 h-2.5 bg-gray-300/80 rounded-md" />
            <div className="w-12 h-2.5 bg-gray-300/80 rounded-md" />
            <div className="w-16 h-2.5 bg-gray-300/80 rounded-md" />
          </div>
          <div className="divide-y divide-gray-100 bg-white">
            {[1, 2, 3].map((row) => (
              <div key={row} className="h-8 flex items-center px-4 justify-between">
                <div className="w-20 h-2.5 bg-gray-200 rounded-md" />
                <div className="w-12 h-2.5 bg-sky-100 rounded-md" />
                <div className="w-12 h-2.5 bg-emerald-100 rounded-md" />
                <div className="w-14 h-2.5 bg-primary/15 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Interactive SVG Chart Sub-Component ────────────────────────── */
const UsageSvgChart = ({ data, type = "area", hoveredPoint, setHoveredPoint }) => {
  const containerRef = useRef(null);

  // Layout measurements
  const height = 220;
  const padding = { top: 20, right: 25, bottom: 35, left: 55 };
  const viewBoxWidth = 650;
  const plotWidth = viewBoxWidth - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const isMultiYear = useMemo(() => {
    if (!data || data.length < 2) return false;
    const firstYear = data[0].date.split("-")[0];
    const lastYear = data[data.length - 1].date.split("-")[0];
    return firstYear !== lastYear;
  }, [data]);

  const { maxVal, yTicks } = useMemo(() => {
    if (!data || data.length === 0) return { maxVal: 100, yTicks: [0, 25, 50, 75, 100] };
    const rawMax = Math.max(...data.map((d) => d.totalUsedToken), 10);
    const targetMax = rawMax * 1.15; // 15% headroom

    // Calculate a nice human-readable step size (1, 2, 5 * 10^k)
    const tickCount = 4;
    const rawStep = targetMax / tickCount;
    const power = Math.pow(10, Math.max(0, Math.floor(Math.log10(rawStep))));
    const fraction = rawStep / power;
    let niceStep;
    if (fraction <= 1) niceStep = 1 * power;
    else if (fraction <= 2) niceStep = 2 * power;
    else if (fraction <= 5) niceStep = 5 * power;
    else niceStep = 10 * power;

    const niceMax = Math.max(niceStep * tickCount, 10);
    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(Math.round(i * niceStep));
    }

    return { maxVal: niceMax, yTicks: ticks };
  }, [data]);

  const slotWidth = useMemo(() => {
    if (!data || data.length === 0) return 56;
    return Math.min(plotWidth / data.length, 56);
  }, [data, plotWidth]);

  // Coordinate mapper - uses slot center for discrete data points / bars
  const getX = (index) => {
    return padding.left + (index + 0.5) * slotWidth;
  };

  const getY = (val) => {
    const ratio = Math.min(1, Math.max(0, val / maxVal));
    return padding.top + plotHeight - ratio * plotHeight;
  };

  // Build SVG path strings for Area & Line
  const { linePath, areaPath, points } = useMemo(() => {
    if (!data || data.length === 0) return { linePath: "", areaPath: "", points: [] };

    const pts = data.map((d, i) => ({
      x: getX(i),
      y: getY(d.totalUsedToken),
      data: d,
      index: i,
    }));

    if (pts.length === 1) {
      return { linePath: "", areaPath: "", points: pts };
    }

    // Generate smooth line path
    let lPath = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      // Catmull-Rom style control points
      const cx1 = prev.x + (curr.x - prev.x) / 2;
      const cy1 = prev.y;
      const cx2 = prev.x + (curr.x - prev.x) / 2;
      const cy2 = curr.y;
      lPath += ` C ${cx1},${cy1} ${cx2},${cy2} ${curr.x},${curr.y}`;
    }

    const aPath = `${lPath} L ${pts[pts.length - 1].x},${padding.top + plotHeight} L ${pts[0].x},${padding.top + plotHeight} Z`;

    return { linePath: lPath, areaPath: aPath, points: pts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, maxVal, slotWidth]);

  // Determine which indices should show X-axis date labels
  const visibleLabelIndices = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (data.length <= 10) {
      return data.map((_, i) => i);
    }
    // For larger datasets (up to 365 days / 1 year), display 7 evenly spaced labels
    const count = 7;
    const step = (data.length - 1) / (count - 1);
    const indices = [];
    for (let i = 0; i < count; i++) {
      indices.push(Math.round(i * step));
    }
    return Array.from(new Set(indices));
  }, [data]);

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${height}`}
        className="w-full h-auto overflow-visible select-none"
      >
        <defs>
          <linearGradient id="primaryAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3D2B90" stopOpacity="0.45" />
            <stop offset="50%" stopColor="#7B61FF" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#3D2B90" stopOpacity="0.0" />
          </linearGradient>

          <linearGradient id="primaryBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7B61FF" />
            <stop offset="100%" stopColor="#3D2B90" />
          </linearGradient>

          <filter id="shadowFilter" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#3D2B90" floodOpacity="0.3" />
          </filter>
        </defs>

        {/* Horizontal Gridlines & Y-Axis Labels */}
        {yTicks.map((tickVal, idx) => {
          const yPos = getY(tickVal);
          return (
            <g key={idx}>
              <line
                x1={padding.left}
                y1={yPos}
                x2={viewBoxWidth - padding.right}
                y2={yPos}
                stroke="#E2E8F0"
                strokeDasharray={idx === 0 ? "none" : "4 4"}
                strokeWidth={idx === 0 ? "1.5" : "1"}
              />
              <text
                x={padding.left - 8}
                y={yPos + 3}
                textAnchor="end"
                className="fill-gray-400 font-roboto text-[10px] font-medium font-mono"
              >
                {formatCompact(tickVal)}
              </text>
            </g>
          );
        })}

        {/* X-Axis bottom base line */}
        <line
          x1={padding.left}
          y1={padding.top + plotHeight}
          x2={viewBoxWidth - padding.right}
          y2={padding.top + plotHeight}
          stroke="#CBD5E1"
          strokeWidth="1.5"
        />

        {/* Chart Rendering: Area Mode */}
        {type === "area" && data.length > 1 && (
          <>
            <path d={areaPath} fill="url(#primaryAreaGrad)" />
            <path
              d={linePath}
              fill="none"
              stroke="#3D2B90"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </>
        )}

        {/* Chart Rendering: Bar Mode */}
        {type === "bar" && (
          <g>
            {data.map((d, i) => {
              const barWidth = Math.max(2, Math.min(32, slotWidth * 0.65));
              const cx = getX(i);
              const xPos = cx - barWidth / 2;
              const yPos = getY(d.totalUsedToken);
              const barHeight = Math.max(2, padding.top + plotHeight - yPos);
              const isHovered = hoveredPoint?.index === i;

              return (
                <rect
                  key={d.date}
                  x={xPos}
                  y={yPos}
                  width={barWidth}
                  height={barHeight}
                  rx={barWidth > 8 ? 6 : barWidth > 4 ? 3 : 1}
                  fill="url(#primaryBarGrad)"
                  opacity={hoveredPoint && !isHovered ? 0.45 : 1}
                  className="transition-all cursor-pointer"
                  onMouseEnter={() => setHoveredPoint({ ...d, index: i, cx, cy: yPos })}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              );
            })}
          </g>
        )}

        {/* Interactive Points on Line/Area */}
        {type === "area" &&
          points.map((pt) => {
            const isHovered = hoveredPoint?.index === pt.index;
            return (
              <g key={pt.data.date}>
                {isHovered && (
                  <line
                    x1={pt.x}
                    y1={padding.top}
                    x2={pt.x}
                    y2={padding.top + plotHeight}
                    stroke="#7B61FF"
                    strokeDasharray="3 3"
                    strokeWidth="1.5"
                  />
                )}
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={isHovered ? 6 : data.length > 30 ? 2.5 : data.length === 1 ? 7 : 4}
                  fill={isHovered ? "#7B61FF" : "#3D2B90"}
                  stroke="#ffffff"
                  strokeWidth={data.length > 30 ? "1.5" : "2.5"}
                  filter="url(#shadowFilter)"
                  className="cursor-pointer transition-all"
                  onMouseEnter={() =>
                    setHoveredPoint({ ...pt.data, index: pt.index, cx: pt.x, cy: pt.y })
                  }
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              </g>
            );
          })}

        {/* X-Axis Date Labels */}
        {visibleLabelIndices.map((i) => {
          const d = data[i];
          if (!d) return null;
          const cx = getX(i);
          const dateLabel = formatChartDate(d.date, isMultiYear);

          return (
            <text
              key={d.date}
              x={cx}
              y={padding.top + plotHeight + 18}
              textAnchor="middle"
              className="fill-gray-500 font-roboto text-[10px] font-medium font-mono"
            >
              {dateLabel}
            </text>
          );
        })}
      </svg>

      {/* Floating Hover Tooltip */}
      {hoveredPoint && (
        <div
          className="absolute z-30 pointer-events-none px-4 py-3 rounded-2xl text-xs font-roboto border transition-all transform -translate-x-1/2 -translate-y-full"
          style={{
            left: `${(hoveredPoint.cx / viewBoxWidth) * 100}%`,
            top: `${Math.max(10, hoveredPoint.cy - 12)}px`,
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(83, 70, 148, 0.2)",
            boxShadow: "0 10px 25px -3px rgba(83, 70, 148, 0.18), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div
            className="flex items-center gap-1.5 font-bold pb-1.5 mb-2 font-mono border-b"
            style={{ color: "#1A0E3E", borderColor: "rgba(83, 70, 148, 0.12)" }}
          >
            <FiCalendar style={{ color: "#534694" }} />
            <span>{formatTooltipDate(hoveredPoint.date)}</span>
          </div>
          <div className="flex flex-col gap-1.5 min-w-[135px]">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold" style={{ color: "#374151" }}>Total Used:</span>
              <span className="font-bold font-mono text-sm" style={{ color: "#D97706" }}>
                {formatNumber(hoveredPoint.totalUsedToken)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium" style={{ color: "#4B5563" }}>Input:</span>
              <span className="font-bold font-mono" style={{ color: "#0284C7" }}>
                {formatNumber(hoveredPoint.inputToken)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium" style={{ color: "#4B5563" }}>Output:</span>
              <span className="font-bold font-mono" style={{ color: "#059669" }}>
                {formatNumber(hoveredPoint.outputToken)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenModal;
